// implement opencv LineSegmentDetector
// src: https://github.com/opencv/opencv/blob/4.x/modules/imgproc/src/lsd.cpp#L420
// paper: https://www.ipol.im/pub/art/2012/gjmr-lsd/article.pdf

import { vec3, vec4 } from "gl-matrix"
import { percentDifference } from "@/lib/ai/calibration/ai-calibration-utils"
import {
	dist,
	distSq,
	rad2Degree,
	getLineEquation,
	distanceFromPointToLine,
	isPerpendicularPointOnSegment,
} from "@/utils/math"
import { startMeasurement } from "@/utils/benchmark"

const CV_PI = Math.PI // PI
const M_3_2_PI = (3 * Math.PI) / 2 // 3/2 pi
const M_2__PI = 2 * Math.PI // 2 pi
// const M_LN10 = 2.30258509299404568402;  // Natural log of 10
const NOTDEF = -1024.0 // Label for pixels with undefined gradient.
const NOTUSED = 0 // Label for pixels not used in yet.
const USED = 1 // Label for pixels already used in detection.
// const RELATIVE_ERROR_FACTOR = 100.0;
// const DEG_TO_RADS = Math.PI / 180;
const QUANT = 2.0
const ANG_TH = 22.5
// const LOG_EPS = 0.0;
const DENSITY_TH = 0.7
const N_BINS = 1024

// Gaussian blur and resize to 0.8x
// const SCALE = 0.8
// const SIGMA_SCALE = 0.6
// const sigma = (SCALE < 1) ? (SIGMA_SCALE / SCALE) : SIGMA_SCALE; // 0.75?
// const sprec = 3;
// const h = Math.ceil(sigma * Math.sqrt(2 * sprec * Math.log(10))); // 4, kernelSize = 9

interface normPoint {
	x: number
	y: number
	norm: number
}

interface RegionPoint {
	x: number
	y: number
	used: number
	angle: number
	modgrad: number
}

interface Rectangle {
	x1: number
	y1: number
	x2: number
	y2: number
	width: number
	x: number
	y: number
	theta: number
	dx: number
	dy: number
	prec: number
	p: number
	intensity: number
	widthStd: number
}

export interface Line {
	r: number
	theta: number
	points: number
	rotation: number
	length: number
	width: number
	intensity: number
	isVertical: boolean
	endPoints: vec4
	lineEq: vec3 // Ax + By + C = 0
	endPointsOnBoundary: vec4
	minDistToCenter: number
	combinedR: number
	confidence?: number
	rScore?: number
	importance?: number
}

export function detectLineSegmentRects(G, Gx, Gy, width, height, scale = 0.8, doRefine = true) {
	const endMeasurement = startMeasurement("detectLineSegmentRects")

	// Angle tolerance
	const prec = (Math.PI * ANG_TH) / 180
	const p = ANG_TH / 180
	const rho = QUANT / Math.sin(prec) // gradient magnitude threshold

	const angles = new Array(width * height)

	let maxGrad = 0
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const index = y * width + x
			const norm = G[index]
			const gx = Gx[index]
			const gy = Gy[index]
			if (norm <= rho) {
				angles[index] = NOTDEF
			} else {
				if (maxGrad < norm) maxGrad = norm
				angles[y * width + x] = Math.atan2(gx, -gy)
			}
		}
	}

	const binCoef = maxGrad > 0 ? (N_BINS - 1) / maxGrad : 0
	let orderedPoints: normPoint[] = []

	for (let y = 0; y < height - 1; y++) {
		for (let x = 0; x < width - 1; x++) {
			const index = y * width + x
			const normValue = G[index]
			const i = Math.floor(normValue * binCoef)

			const point: normPoint = {
				x: x,
				y: y,
				norm: i,
			}

			orderedPoints.push(point)
		}
	}

	// Sort the points based on the norm value.
	// orderedPoints.sort((a, b) => b.norm - a.norm)
	// Todo check speed
	orderedPoints = bucketSort(orderedPoints)

	const LOG_NT = (5 * (Math.log10(width) + Math.log10(height))) / 2 + Math.log10(11.0)
	const minRegSize = -LOG_NT / Math.log10(p)

	let used = Array(width * height).fill(NOTUSED) // Assuming a 1D array for the image.

	const rects: Rectangle[] = []
	for (const point of orderedPoints) {
		if (
			used[point.y * width + point.x] === NOTUSED &&
			angles[point.y * width + point.x] !== NOTDEF
		) {
			const result = regionGrow(point, prec, width, height, used, angles, G)
			const reg = result.reg
			used = result.used
			const regAngle = result.regAngle

			if (reg.length < minRegSize) continue

			let rec = region2rect(reg, regAngle, prec, p)

			if (doRefine) {
				const refined = refine(
					reg,
					regAngle,
					prec,
					p,
					rec,
					DENSITY_TH,
					used,
					angles,
					G,
					width,
					height,
				)
				used = refined.used

				if (!refined.valid) continue
				rec = refined.rec
			}

			// Add the offset for sobel2x2
			rec.x1 += 0.5
			rec.y1 += 0.5
			rec.x2 += 0.5
			rec.y2 += 0.5

			if (scale !== 1) {
				rec.x1 /= scale
				rec.y1 /= scale
				rec.x2 /= scale
				rec.y2 /= scale
				rec.width /= scale
			}

			rects.push(rec)
		}
	}

	endMeasurement()
	return rects
}

function bucketSort(points: normPoint[]) {
	const buckets: normPoint[][] = Array.from({ length: N_BINS }, () => [])

	// Distribute the points into buckets
	for (const point of points) {
		const bucket = buckets[point.norm] as normPoint[]
		bucket.push(point)
	}

	// Flatten the buckets back into a single array in reverse order
	let sortedPoints: normPoint[] = []
	for (let i = buckets.length - 1; i >= 0; i--) {
		sortedPoints = sortedPoints.concat(buckets[i] as normPoint[])
	}

	return sortedPoints
}

function regionGrow(
	s: normPoint,
	prec: number,
	imgWidth: number,
	imgHeight: number,
	used: number[],
	angles: number[],
	modgrad: number[],
) {
	const reg: RegionPoint[] = []

	// Point to this region
	const seed: RegionPoint = {
		x: s.x,
		y: s.y,
		used: used[s.y * imgWidth + s.x] as number, // Adjust as needed
		angle: angles[s.y * imgWidth + s.x] as number,
		modgrad: modgrad[s.y * imgWidth + s.x] as number,
	}
	let regAngle = seed.angle
	reg.push(seed)

	let sumdx = Math.cos(regAngle)
	let sumdy = Math.sin(regAngle)

	// Assume USED = 1 as per previous context
	used[s.y * imgWidth + s.x] = USED

	// Try neighboring regions
	for (const rpoint of reg) {
		const xx_min = Math.max(rpoint.x - 1, 0)
		const xx_max = Math.min(rpoint.x + 1, imgWidth - 1)
		const yy_min = Math.max(rpoint.y - 1, 0)
		const yy_max = Math.min(rpoint.y + 1, imgHeight - 1)

		for (let yy = yy_min; yy <= yy_max; ++yy) {
			for (let xx = xx_min; xx <= xx_max; ++xx) {
				const index = yy * imgWidth + xx
				if (
					used[index] !== USED &&
					isAligned(xx, yy, regAngle, prec, angles, imgWidth, imgHeight)
				) {
					const angle = angles[index] as number

					// Add point
					used[index] = USED
					const regionPoint: RegionPoint = {
						x: xx,
						y: yy,
						used: used[index],
						angle: angle,
						modgrad: modgrad[index] as number,
					}
					reg.push(regionPoint)

					// Update region's angle
					sumdx += Math.cos(angle)
					sumdy += Math.sin(angle)
					regAngle = Math.atan2(sumdy, sumdx)
				}
			}
		}
	}

	return { reg, regAngle, used }
}

function isAligned(
	x: number,
	y: number,
	theta: number,
	prec: number,
	angles: number[],
	imgWidth: number,
	imgHeight: number,
): boolean {
	if (x < 0 || y < 0 || x >= imgWidth || y >= imgHeight) {
		return false
	}

	const index = y * imgWidth + x
	const a = angles[index] as number

	if (a === NOTDEF) {
		return false
	}

	// It is assumed that 'theta' and 'a' are in the range [-pi,pi]
	let n_theta = theta - a
	if (n_theta < 0) {
		n_theta = -n_theta
	}
	if (n_theta > M_3_2_PI) {
		n_theta -= M_2__PI
		if (n_theta < 0) {
			n_theta = -n_theta
		}
	}

	return n_theta <= prec
}

function region2rect(reg: RegionPoint[], regAngle: number, prec: number, p: number) {
	let x = 0,
		y = 0,
		sum = 0
	for (const pnt of reg) {
		const weight = pnt.modgrad
		x += pnt.x * weight
		y += pnt.y * weight
		sum += weight
	}

	// Weighted sum must differ from 0
	if (!(sum > 0)) {
		throw new Error("Assertion failed: sum > 0")
	}

	x /= sum
	y /= sum

	const theta = getTheta(reg, x, y, regAngle, prec)

	// Find length and width
	const dx = Math.cos(theta)
	const dy = Math.sin(theta)
	let l_min = 0,
		l_max = 0,
		w_min = 0,
		w_max = 0
	let w_sq_mean = 0
	let w_mean = 0
	for (const rpoint of reg) {
		const regdx = rpoint.x - x
		const regdy = rpoint.y - y

		const l = regdx * dx + regdy * dy
		const w = -regdx * dy + regdy * dx

		if (l > l_max) l_max = l
		else if (l < l_min) l_min = l
		if (w > w_max) w_max = w
		else if (w < w_min) w_min = w

		w_sq_mean += w * w
		w_mean += w
	}
	w_mean /= reg.length
	w_sq_mean /= reg.length

	// Populate the Rectangle object
	const rec: Rectangle = {
		x1: x + l_min * dx,
		y1: y + l_min * dy,
		x2: x + l_max * dx,
		y2: y + l_max * dy,
		width: w_max - w_min,
		x: x,
		y: y,
		theta: theta,
		dx: dx,
		dy: dy,
		prec: prec,
		p: p,
		intensity: (sum / reg.length) * 4, // Yuhe: * 4 for sobel2x2
		widthStd: Math.sqrt(w_sq_mean - w_mean * w_mean),
	}

	// Min width of 1 pixel
	if (rec.width < 1.0) rec.width = 1.0

	return rec
}

function getTheta(
	reg: RegionPoint[],
	x: number,
	y: number,
	regAngle: number,
	prec: number,
): number {
	let Ixx = 0.0
	let Iyy = 0.0
	let Ixy = 0.0

	// Compute inertia matrix
	for (const rpoint of reg) {
		const regx = rpoint.x
		const regy = rpoint.y
		const weight = rpoint.modgrad
		const dx = regx - x
		const dy = regy - y
		Ixx += dy * dy * weight
		Iyy += dx * dx * weight
		Ixy -= dx * dy * weight
	}

	// // Check if inertia matrix is null
	// if (doubleEqual(Ixx, 0) && doubleEqual(Iyy, 0) && doubleEqual(Ixy, 0)) {
	//     throw new Error("Assertion failed: Inertia matrix is null.");
	// }

	// Compute smallest eigenvalue
	const lambda = 0.5 * (Ixx + Iyy - Math.sqrt((Ixx - Iyy) * (Ixx - Iyy) + 4.0 * Ixy * Ixy))

	// Compute angle
	let theta =
		Math.abs(Ixx) > Math.abs(Iyy)
			? Math.atan2(lambda - Ixx, Ixy)
			: Math.atan2(Ixy, lambda - Iyy)
	// atan2 returns range [-pi, pi]

	// Correct angle by 180 deg if necessary
	if (angleDiff(theta, regAngle) > prec) {
		theta += Math.PI
	}

	return theta
}

// Signed angle difference
function angleDiffSigned(a: number, b: number): number {
	let diff = a - b
	while (diff <= -CV_PI) diff += M_2__PI
	while (diff > CV_PI) diff -= M_2__PI
	return diff
}

// Absolute value angle difference
export function angleDiff(a: number, b: number): number {
	return Math.abs(angleDiffSigned(a, b))
}

function refine(
	reg: RegionPoint[],
	regAngle: number,
	prec: number,
	p: number,
	rec: Rectangle,
	densityTh: number,
	used: number[],
	angles: number[],
	G: number[],
	imgWidth: number,
	imgHeight: number,
) {
	const density = reg.length / (dist(rec.x1, rec.y1, rec.x2, rec.y2) * rec.width)

	// logLineDetection("refine start", {reg, rec, density, prec})

	if (density >= densityTh) {
		return { valid: true, rec: rec, used: used }
	}

	const reg0 = reg[0] as RegionPoint
	const xc = reg0.x
	const yc = reg0.y
	const angC = reg0.angle
	let sum = 0
	let sSum = 0
	let n = 0

	for (const regPoint of reg) {
		// regPoint.used = NOTUSED
		// used[regPoint.y * imgWidth + regPoint.x] = NOTUSED
		if (dist(xc, yc, regPoint.x, regPoint.y) < rec.width) {
			const angle = regPoint.angle
			const angD = angleDiffSigned(angle, angC)
			sum += angD
			sSum += angD * angD
			n++
		}
	}

	if (n <= 0) {
		// logLineDetection("refine invalid n<=0", {n})

		for (const regPoint of reg) {
			regPoint.used = NOTUSED
			used[regPoint.y * imgWidth + regPoint.x] = NOTUSED
		}
		return { valid: false, rec: rec, used: used }
		// throw new Error("Assertion failed: n > 0")
	}

	const meanAngle = sum / n
	const tau = 2.0 * Math.sqrt(sSum / n - meanAngle * meanAngle)

	// Try new region with smaller angle thresh tau
	// Yuhe: don't do this for tau > prec
	// logLineDetection("refine tau", {tau})
	let newDensity = density
	if (tau < prec) {
		for (const regPoint of reg) {
			regPoint.used = NOTUSED
			used[regPoint.y * imgWidth + regPoint.x] = NOTUSED
		}
		const reg0 = reg[0] as RegionPoint
		const seed: normPoint = { x: reg0.x, y: reg0.y, norm: 1 }
		const result = regionGrow(seed, tau, imgWidth, imgHeight, used, angles, G)
		reg = result.reg
		used = result.used

		if (reg.length < 2) {
			// logLineDetection("refine tau invalid", {reg, density, tau})
			return { valid: false, rec: rec, used: used }
		}

		rec = region2rect(reg, regAngle, prec, p)
		newDensity = reg.length / (dist(rec.x1, rec.y1, rec.x2, rec.y2) * rec.width)
		// logLineDetection("refine tau end", {reg, rec, density, newDensity, prec, tau})
	}

	if (newDensity < densityTh) {
		return reduceRegionWidthAndRadius(
			reg,
			regAngle,
			prec,
			p,
			rec,
			densityTh,
			used,
			angles,
			G,
			imgWidth,
			imgHeight,
		)
	} else {
		// logLineDetection("refine tau success", {reg, newDensity, tau})
		return { valid: true, rec: rec, used: used }
	}
}

function reduceRegionWidth(
	reg: RegionPoint[],
	regAngle: number,
	prec: number,
	p: number,
	rec: Rectangle,
	density: number,
	used: number[],
	angles: number[],
	G: number[],
	imgWidth: number,
	imgHeight: number,
) {
	// Remove points from the region and update 'used' map

	const widthThresh = Math.max(rec.widthStd * 2, 2)
	// let log = false
	// if (reg.length > 1000) log = true
	// if (log) logLineDetection("~~reduceRegionWidth start", {reg, rec, widthThresh, density})

	if (widthThresh > rec.width / 2) return { valid: true, rec, used, reg, density }

	const dx = Math.cos(rec.theta)
	const dy = Math.sin(rec.theta)
	const x = rec.x
	const y = rec.y
	for (let i = 0; i < reg.length; i++) {
		const regi = reg[i] as RegionPoint
		const regdx = regi.x - x
		const regdy = regi.y - y

		const w = -regdx * dy + regdy * dx

		if (Math.abs(w) > widthThresh) {
			// Remove point from the region
			regi.used = NOTUSED
			used[regi.y * imgWidth + regi.x] = NOTUSED
			reg[i] = reg[reg.length - 1] as RegionPoint
			reg.pop()
			i-- // To avoid skipping one point
		}
	}

	if (reg.length < 2) {
		// logLineDetection("~~reduceRegionWidth invalid", {reg, rec, density})
		return { valid: false, rec, used, reg, density }
	}
	// if (log) logLineDetection("~~reduceRegionWidth middle", {reg, rec})

	// Re-do region grow to avoid returning disconnected line after width reduction
	const usedInReg = new Array(used.length).fill(NOTUSED)
	for (const p of reg) {
		const index = p.y * imgWidth + p.x
		usedInReg[index] = USED
		used[index] = NOTUSED
	}
	const reg0 = reg[0] as RegionPoint
	const newReg: RegionPoint[] = [reg0]
	const index0 = reg0.y * imgWidth + reg0.x
	used[index0] = USED
	for (const rpoint of newReg) {
		const xx_min = Math.max(rpoint.x - 1, 0)
		const xx_max = Math.min(rpoint.x + 1, imgWidth - 1)
		const yy_min = Math.max(rpoint.y - 1, 0)
		const yy_max = Math.min(rpoint.y + 1, imgHeight - 1)

		for (let yy = yy_min; yy <= yy_max; ++yy) {
			for (let xx = xx_min; xx <= xx_max; ++xx) {
				const index = yy * imgWidth + xx
				if (usedInReg[index] === USED && used[index] === NOTUSED) {
					// Add point
					used[index] = USED
					const regionPoint: RegionPoint = {
						x: xx,
						y: yy,
						used: used[index],
						angle: angles[index] as number,
						modgrad: G[index] as number,
					}
					newReg.push(regionPoint)
				}
			}
		}
	}
	reg = newReg

	// Re-compute rectangle
	rec = region2rect(reg, regAngle, prec, p)

	// Re-compute region points density
	density = reg.length / (dist(rec.x1, rec.y1, rec.x2, rec.y2) * rec.width)

	// if (log) logLineDetection("~~reduceRegionWidth end", {reg, rec, density})

	return { valid: true, rec, used, reg, density }
}

function reduceRegionWidthAndRadius(
	reg: RegionPoint[],
	regAngle: number,
	prec: number,
	p: number,
	rec: Rectangle,
	densityTh: number,
	used: number[],
	angles: number[],
	G: number[],
	imgWidth: number,
	imgHeight: number,
	reduceWidthIters: number = 0, // disable width reduction by default
) {
	let density = reg.length / (dist(rec.x1, rec.y1, rec.x2, rec.y2) * rec.width)

	// First try to reduce region width for at most 2 iterations if the line is straight (width small)
	let iter = 0
	let lwRatio = dist(rec.x1, rec.y1, rec.x2, rec.y2) / rec.width
	let stdRatio = rec.widthStd / (rec.width / 4)
	while (
		rec.width < 23 &&
		lwRatio > 10 &&
		stdRatio < 0.9 &&
		density < densityTh &&
		iter < reduceWidthIters
	) {
		const result = reduceRegionWidth(
			reg,
			regAngle,
			prec,
			p,
			rec,
			density,
			used,
			angles,
			G,
			imgWidth,
			imgHeight,
		)
		if (!result.valid) {
			return { valid: false, rec: result.rec, used: result.used }
		}
		used = result.used
		rec = result.rec
		reg = result.reg
		density = result.density
		lwRatio = dist(rec.x1, rec.y1, rec.x2, rec.y2) / rec.width
		stdRatio = rec.widthStd / (rec.width / 4)
		iter++

		if (density >= densityTh) return { valid: true, rec: rec, used: used }
	}

	// Compute region's radius
	const reg0 = reg[0] as RegionPoint
	const xc = reg0.x
	const yc = reg0.y
	const radSq1 = distSq(xc, yc, rec.x1, rec.y1)
	const radSq2 = distSq(xc, yc, rec.x2, rec.y2)
	let radSq = radSq1 > radSq2 ? radSq1 : radSq2
	// logLineDetection("reduceRegionRadius start", {reg, rec, density, radSq})

	while (density < densityTh) {
		radSq *= 0.75 * 0.75 // Reduce region's radius to 75% of its value

		// Remove points from the region and update 'used' map
		for (let i = 0; i < reg.length; i++) {
			const regi = reg[i] as RegionPoint
			if (distSq(xc, yc, regi.x, regi.y) > radSq) {
				// Remove point from the region
				regi.used = NOTUSED
				used[regi.y * imgWidth + regi.x] = NOTUSED
				reg[i] = reg[reg.length - 1] as RegionPoint
				reg.pop()
				i-- // To avoid skipping one point
			}
		}

		if (reg.length < 2) {
			// logLineDetection("reduceRegionRadius invalid", {reg, rec, density})

			return { valid: false, rec: rec, used: used }
		}

		// Re-compute rectangle
		rec = region2rect(reg, regAngle, prec, p)

		// Re-compute region points density
		density = reg.length / (dist(rec.x1, rec.y1, rec.x2, rec.y2) * rec.width)
		// logLineDetection("reduceRegionRadius iteration", {reg, rec, density})
	}

	// logLineDetection("reduceRegionRadius succeed", {reg, rec, density})

	return { valid: true, rec: rec, used: used }
}

export function thetaToRotation(theta) {
	let isVertical = false
	let rotation = 0
	if (Math.abs(theta - 90) < 45) {
		isVertical = true
		rotation = 90 - theta
	} else {
		isVertical = false
		rotation = theta < 90 ? -theta : 180 - theta
	}

	return { rotation, isVertical }
}

function getEndPointsOnBoundary(
	lineEq: vec3,
	isVertical: boolean,
	imgWidth: number,
	imgHeight: number,
): vec4 {
	// line eq: Ax + By + C = 0
	const [A, B, C] = lineEq
	if (isVertical) {
		const xTop = -C / A
		const xBottom = (-C - B * (imgHeight - 1)) / A
		const endPoints: vec4 = [xTop, 0, xBottom, imgHeight - 1]
		return endPoints
	} else {
		const yLeft = -C / B
		const yRight = (-C - A * (imgWidth - 1)) / B
		const endPoints: vec4 = [0, yLeft, imgWidth - 1, yRight]
		return endPoints
	}
}

export function getLineSegments(
	rects: Rectangle[],
	imgWidth: number,
	imgHeight: number,
	lengthThresh: number,
	angleThresh: number = 25,
	intensityThresh = 20,
	widthThresh = 16,
) {
	const longLines: Line[] = []

	for (const rec of rects) {
		if (rec.intensity < intensityThresh) continue

		const length = dist(rec.x1, rec.y1, rec.x2, rec.y2)
		if (length < lengthThresh) continue

		if (rec.width > widthThresh && length < 150) continue

		let theta = rad2Degree(rec.theta)
		// convert [-180, 360] to [0, 180]
		if (theta < 0) {
			theta += 180
		}
		if (theta > 180) {
			theta -= 180
		}
		const { rotation, isVertical } = thetaToRotation(theta)
		if (Math.abs(rotation) > angleThresh) continue

		const centerX = imgWidth / 2
		const centerY = imgHeight / 2
		const endPoints: vec4 = [rec.x1, rec.y1, rec.x2, rec.y2]
		const lineEq: vec3 = getLineEquation(endPoints)
		const r = distanceFromPointToLine([centerX, centerY], lineEq)
		const minDistToEnds = Math.min(
			dist(rec.x1, rec.y1, centerX, centerY),
			dist(rec.x2, rec.y2, centerX, centerY),
		)
		const isOnSegment = isPerpendicularPointOnSegment([centerX, centerY], endPoints)
		const minDistToCenter = isOnSegment ? r : minDistToEnds
		const line: Line = {
			r: r,
			theta: theta,
			points: length,
			rotation: rotation,
			isVertical: isVertical,
			length: length,
			width: rec.width,
			intensity: rec.intensity,
			endPoints: endPoints,
			lineEq: lineEq,
			endPointsOnBoundary: getEndPointsOnBoundary(lineEq, isVertical, imgWidth, imgHeight),
			minDistToCenter: minDistToCenter,
			combinedR: r * 0.6 + minDistToCenter * 0.4,
		}

		longLines.push(line)
	}

	return longLines
}

function getCombinedTheta(line1: Line, line2: Line) {
	const w1 = line1.points / (line1.points + line2.points)
	const w2 = 1 - w1
	let theta1 = line1.theta
	let theta2 = line2.theta
	if (Math.abs(theta1 - theta2) > 90) {
		if (theta1 > theta2) theta2 += 180
		else theta1 += 180
	}
	let newTheta = w1 * theta1 + w2 * theta2
	if (newTheta > 180) newTheta -= 180
	return newTheta
}

function isSameLine(line1: Line, line2: Line, thresh = 3) {
	if (Math.abs(line1.rotation - line2.rotation) > 0.8) return false
	if (Math.abs(line1.r - line2.r) > 1.5) return false
	if (Math.abs(line1.width - line2.width) > 10) return false
	if (
		percentDifference(line1.intensity, line2.intensity) > 0.6 &&
		Math.abs(line1.intensity - line2.intensity) > 60
	)
		return false

	const d1 =
		Math.abs(line1.endPointsOnBoundary[0] - line2.endPointsOnBoundary[0]) +
		Math.abs(line1.endPointsOnBoundary[1] - line2.endPointsOnBoundary[1])

	const d2 =
		Math.abs(line1.endPointsOnBoundary[2] - line2.endPointsOnBoundary[2]) +
		Math.abs(line1.endPointsOnBoundary[3] - line2.endPointsOnBoundary[3])

	if (d1 < thresh && d2 < thresh) return true
	else return false
}

function combineLines(existingLine: Line, line: Line) {
	const newLine: Line = { ...existingLine }
	const w1 = existingLine.points / (existingLine.points + line.points)
	const w2 = 1 - w1
	// sort to make sure endPointsOnBoundary is before endPoints
	const keys = Object.keys(existingLine).sort((a, b) => b.localeCompare(a))
	for (const key of keys) {
		if (key === "length" || key === "lineEq") continue
		if (key === "theta") {
			newLine[key] = getCombinedTheta(existingLine, line)
		} else if (key === "points") {
			newLine[key] = existingLine[key] + line[key]
		} else if (key === "minDistToCenter" || key === "combinedR") {
			newLine[key] = Math.min(existingLine[key], line[key])
		} else if (typeof existingLine[key] === "number") {
			newLine[key] = existingLine[key] * w1 + line[key] * w2
		} else if (key === "endPointsOnBoundary") {
			// vec4
			const endPointsOnBoundary: vec4 = [0, 0, 0, 0]
			for (let i = 0; i < 4; i++) {
				endPointsOnBoundary[i] =
					(existingLine[key][i] as number) * w1 + (line[key][i] as number) * w2
			}
			newLine[key] = endPointsOnBoundary
		} else if (key === "endPoints") {
			const [x11, y11, x12, y12] = existingLine.endPoints as [number, number, number, number]
			const [x21, y21, x22, y22] = line.endPoints as [number, number, number, number]
			// line eq: Ax + By + C = 0
			const [x1, y1, x2, y2] = newLine.endPointsOnBoundary as [number, number, number, number]
			newLine["lineEq"] = getLineEquation([x1, y1, x2, y2])
			const [A, B, C] = newLine["lineEq"] as [number, number, number]

			if (existingLine.isVertical) {
				const minY = Math.min(y11, y12, y21, y22)
				const maxY = Math.max(y11, y12, y21, y22)
				const minX = (-C - B * minY) / A
				const maxX = (-C - B * maxY) / A
				newLine[key] = [minX, minY, maxX, maxY]
				newLine["length"] = dist(minX, minY, maxX, maxY)
			} else {
				const minX = Math.min(x11, x12, x21, x22)
				const maxX = Math.max(x11, x12, x21, x22)
				const minY = (-C - A * minX) / B
				const maxY = (-C - A * maxX) / B
				newLine[key] = [minX, minY, maxX, maxY]
				newLine["length"] = dist(minX, minY, maxX, maxY)
			}
		}
	}
	// logLineDetection("combined line rotation, points, length, endPoints:",
	//     newLine.rotation, newLine.points, newLine.length, newLine.endPoints)
	// logLineDetection("combined lines:", { existingLine, line, newLine })

	return newLine
}

export function getCombinedLines(lineSegments: Line[], pointsThresh: number): Line[] {
	lineSegments.sort((a, b) => b.length - a.length)

	const lines: Line[] = []
	for (const line of lineSegments) {
		let foundSameLine = false
		for (let i = 0; i < lines.length; i++) {
			const existingLine = lines[i] as Line
			if (isSameLine(existingLine, line)) {
				lines[i] = combineLines(existingLine, line)
				foundSameLine = true
				break
			}
		}
		if (!foundSameLine) lines.push(line)
	}

	return lines.filter((line) => line.points > pointsThresh)
}
